import { motion } from "framer-motion";
import { Check, CheckCheck, Phone, Video } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { openWhatsApp } from "@/lib/whatsapp";

function ChatBubble({ side = "in", children, time, ticks }) {
  const isOut = side === "out";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div
        className={`relative max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug shadow-sm ${
          isOut ? "bg-[#D9FDD3] text-[#0B1F14] rounded-tr-sm" : "bg-white text-[#111] rounded-tl-sm"
        }`}
      >
        {children}
        <span className="mt-1 flex items-center justify-end gap-1 text-[10px] text-[#5B6b60]">
          {time}
          {isOut && ticks && <CheckCheck className="h-3 w-3 text-[#34B7F1]" />}
        </span>
      </div>
    </div>
  );
}

export default function ConversionBlock() {
  return (
    <section
      id="conversion"
      className="relative overflow-hidden bg-[#381B59] py-20 sm:py-28"
      data-testid="conversion-block"
    >
      {/* decorative glows */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-80 w-80 rounded-full bg-[#5B2C91]/50 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-[#D4F84A]/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          {/* LEFT — copy + CTA */}
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-heading max-w-2xl text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white"
              data-testid="conversion-title"
            >
              Hai bisogno di un medico senza uscire di casa?
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-white/75"
              data-testid="conversion-subtitle"
            >
              Verifica la disponibilità di un infermiere con TytoCare e di un medico generalista o
              specialista nella tua zona.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-9"
            >
              <button
                onClick={() => openWhatsApp()}
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-[#D4F84A] px-8 py-5 text-base sm:text-lg font-bold text-[#1F152E] shadow-[0_20px_50px_rgba(212,248,74,0.3)] hover:-translate-y-1 hover:shadow-[0_26px_60px_rgba(212,248,74,0.42)] transition-[transform,box-shadow] duration-200"
                data-testid="conversion-whatsapp-btn"
              >
                <FaWhatsapp className="h-6 w-6" />
                Richiedi la visita su WhatsApp
              </button>
              <p className="mt-4 text-sm text-white/60" data-testid="conversion-note">
                Nessun pagamento prima della conferma di disponibilità e prezzo.
              </p>
            </motion.div>
          </div>

          {/* RIGHT — WhatsApp chat mockup */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -1.5 }}
              animate={{ opacity: 1, y: 0, rotate: -1.5 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto w-full max-w-[340px]"
              data-testid="conversion-chat-mockup"
            >
              <div className="rounded-[2.5rem] bg-black p-2.5 shadow-[0_40px_80px_rgba(0,0,0,0.45)] ring-1 ring-white/10">
                <div className="overflow-hidden rounded-[2rem] bg-white">
                  {/* WhatsApp header */}
                  <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-sm font-bold">
                      T
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">TytoCare Italia</p>
                      <p className="text-[11px] text-white/70">online</p>
                    </div>
                    <Video className="h-[18px] w-[18px] opacity-90" />
                    <Phone className="h-4 w-4 opacity-90" />
                  </div>

                  {/* Chat body */}
                  <div className="wa-chat-bg space-y-2 px-3 py-4">
                    <div className="mx-auto w-fit rounded-lg bg-[#FFF6D6] px-3 py-1 text-[10px] text-[#5B5647] shadow-sm">
                      Oggi
                    </div>
                    <ChatBubble side="out" time="09:41" ticks>
                      Salve, avrei bisogno di una visita con un cardiologo il prima possibile.
                    </ChatBubble>
                    <ChatBubble side="in" time="09:41">
                      Salve! Verifichiamo subito la disponibilità di un cardiologo nella sua zona.
                    </ChatBubble>
                    <ChatBubble side="in" time="09:42">
                      Infermiere disponibile <b>domani alle 10:30</b>. Cardiologo in diretta. Le
                      confermo il prezzo prima della visita.
                    </ChatBubble>
                    <ChatBubble side="out" time="09:43" ticks>
                      Perfetto, confermo 🙌
                    </ChatBubble>
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm bg-[#5B2C91] px-3.5 py-2 text-[12px] font-semibold text-white shadow-sm">
                        <Check className="h-3.5 w-3.5 text-[#D4F84A]" strokeWidth={3} />
                        Infermiere assegnato · Giulia R.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
